import React from 'react';
import { useState } from 'owa-react';
import { getItem, setItem, type LocalStorageKeys } from 'owa-local-storage';
import { observer } from 'owa-mobx-react';

export type StatusBarField =
    | 'items'
    | 'unread'
    | 'reminders'
    | 'viewShortcuts'
    | 'zoomSlider'
    | 'zoom';
export const statusBarCustomizationFields: StatusBarField[] = [
    'items',
    'unread',
    'reminders',
    'viewShortcuts',
    'zoomSlider',
    'zoom',
];
type StatusBarVisibility = Record<StatusBarField, boolean>;
type StatusBarCountValues = { items?: number; unread?: number };

type StatusBarCustomizationContextValue = {
    visibility: StatusBarVisibility;
    counts: StatusBarCountValues;
    updateVisibility: (field: StatusBarField, visible: boolean) => void;
    updateCounts: (counts: StatusBarCountValues) => void;
};

const defaultVisibility: StatusBarVisibility = {
    items: true,
    unread: true,
    reminders: true,
    viewShortcuts: true,
    zoomSlider: true,
    zoom: true,
};

function storageKey(field: StatusBarField): LocalStorageKeys {
    return `StatusBarVisible_${field}`;
}

// Restore and persist field visibility; only an explicitly saved 'false' hides a field.
function useFieldVisibility(field: StatusBarField): [boolean, (visible: boolean) => void] {
    const [visible, setVisible] = useState(() => getItem(window, storageKey(field)) !== 'false');
    const updateVisibility = React.useCallback(
        (nextVisible: boolean) => {
            setItem(window, storageKey(field), String(nextVisible));
            setVisible(nextVisible);
        },
        [field]
    );
    return [visible, updateVisibility];
}

const StatusBarCustomizationContext = React.createContext<StatusBarCustomizationContextValue>({
    visibility: defaultVisibility,
    counts: {},
    updateVisibility: () => undefined,
    updateCounts: () => undefined,
});

// Share saved visibility and live counts with the bar and portaled module content.
export const StatusBarCustomizationContextProvider: React.FunctionComponent<React.PropsWithChildren> =
    observer(function StatusBarCustomizationContextProvider({ children }) {
        const [itemsVisible, setItemsVisible] = useFieldVisibility('items');
        const [unreadVisible, setUnreadVisible] = useFieldVisibility('unread');
        const [remindersVisible, setRemindersVisible] = useFieldVisibility('reminders');
        const [viewShortcutsVisible, setViewShortcutsVisible] = useFieldVisibility('viewShortcuts');
        const [zoomSliderVisible, setZoomSliderVisible] = useFieldVisibility('zoomSlider');
        const [zoomVisible, setZoomVisible] = useFieldVisibility('zoom');
        const [items, setItems] = useState<number>();
        const [unread, setUnread] = useState<number>();
        const visibility = React.useMemo(
            () => ({
                items: itemsVisible,
                unread: unreadVisible,
                reminders: remindersVisible,
                viewShortcuts: viewShortcutsVisible,
                zoomSlider: zoomSliderVisible,
                zoom: zoomVisible,
            }),
            [
                itemsVisible,
                unreadVisible,
                remindersVisible,
                viewShortcutsVisible,
                zoomSliderVisible,
                zoomVisible,
            ]
        );
        const setters = React.useMemo(
            () => ({
                items: setItemsVisible,
                unread: setUnreadVisible,
                reminders: setRemindersVisible,
                viewShortcuts: setViewShortcutsVisible,
                zoomSlider: setZoomSliderVisible,
                zoom: setZoomVisible,
            }),
            [
                setItemsVisible,
                setUnreadVisible,
                setRemindersVisible,
                setViewShortcutsVisible,
                setZoomSliderVisible,
                setZoomVisible,
            ]
        );
        const updateVisibility = React.useCallback(
            (field: StatusBarField, visible: boolean) => setters[field](visible),
            [setters]
        );
        // Replace both counts so a module without an unread count clears the previous value.
        const updateCounts = React.useCallback((nextCounts: StatusBarCountValues) => {
            setItems(nextCounts.items);
            setUnread(nextCounts.unread);
        }, []);
        const counts = React.useMemo(() => ({ items, unread }), [items, unread]);
        const value = React.useMemo(
            () => ({ visibility, counts, updateVisibility, updateCounts }),
            [visibility, counts, updateVisibility, updateCounts]
        );

        return (
            <StatusBarCustomizationContext.Provider value={value}>
                {children}
            </StatusBarCustomizationContext.Provider>
        );
    }, 'StatusBarCustomizationContextProvider');

export function useStatusBarCustomization(): StatusBarCustomizationContextValue {
    return React.useContext(StatusBarCustomizationContext);
}
